/**
 * Alimente la checklist de découverte (cf. `@core/lib/discoveryChecklist`).
 *
 * Deux décomptes, en `head: true` : PostgREST renvoie l'en-tête `Content-Range`
 * sans transporter une seule ligne. Les deux tables portent une policy RLS
 * `creator_id = auth.uid()` en SELECT, donc le filtre de sécurité est celui de
 * la base, pas celui qu'on écrit ici — le `.eq()` explicite ne sert qu'à rendre
 * l'intention lisible et à ne pas dépendre du comportement de la policy.
 *
 * Volontairement pas dérivé des sondages déjà chargés par le tableau de bord :
 * ceux-ci sont paginés et filtrés, donc « aucune série dans la page courante »
 * n'est pas « aucune série ». Une checklist qui se coche et se décoche selon la
 * page serait pire que pas de checklist.
 */

import { useEffect, useState } from "react";
import { supabase } from "@core/lib/supabase";
import { useAuth } from "@core/contexts/AuthContext";
import { logger } from "@core/lib/logger";
import { hasPaidAccess, canCreateGroup } from "@core/constants/quotas";
import {
  deriveDiscoverySteps,
  type DiscoveryStep,
} from "@core/lib/discoveryChecklist";

export function useDiscoveryChecklist(): DiscoveryStep[] | null {
  const { user, profile } = useAuth();
  const [groups, setGroups] = useState<number | null>(null);
  const [recurringSeries, setRecurringSeries] = useState<number | null>(null);

  useEffect(() => {
    if (!user) {
      setGroups(null);
      setRecurringSeries(null);
      return;
    }

    let cancelled = false;

    const compter = async (table: string): Promise<number | null> => {
      const { count, error } = await supabase
        .from(table)
        .select("id", { count: "exact", head: true })
        .eq("creator_id", user.id);
      if (error) {
        // Une checklist absente est un non-événement ; une checklist fausse
        // ne l'est pas. En cas d'erreur on ne devine pas, on n'affiche rien.
        logger.warn(
          `Discovery checklist: count failed on ${table}`,
          "quota",
          error,
        );
        return null;
      }
      return count ?? 0;
    };

    void (async () => {
      const [g, r] = await Promise.all([
        compter("date_poll_groups"),
        compter("recurring_series"),
      ]);
      if (cancelled) return;
      setGroups(g);
      setRecurringSeries(r);
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) return null;

  return deriveDiscoverySteps(
    { groups, recurringSeries },
    {
      // LE DÉCOMPTE EST INDISPENSABLE ICI, pas décoratif.
      //
      // `canCreateGroup(profile)` sans second argument retombe sur
      // `if (groupesHorsEssai === undefined) return false` : elle répond NON à
      // tout compte non payant, y compris à celui qui a droit à son groupe
      // offert (FREE_USABLE_GROUPS = 1, décision du 18/09). La carte affichait
      // donc « Solo » et renvoyait aux tarifs pour une fonctionnalité que la
      // personne pouvait utiliser gratuitement — dans le composant dont le
      // métier est justement de faire découvrir les groupes.
      //
      // `useGroups` passe déjà `groups.length` ; les deux endroits doivent
      // répondre la même chose à la même question, sinon la carte contredit la
      // page vers laquelle elle mène.
      //
      // `groups` peut valoir null (décompte inconnu) : `?? undefined` redonne
      // alors le refus prudent, et `deriveDiscoverySteps` ne rend rien de toute
      // façon tant qu'un décompte manque.
      canCreateGroup: canCreateGroup(profile, groups ?? undefined),
      canCreateRecurring: hasPaidAccess(profile),
    },
  );
}
